'use client';

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import type { ComparisonFeature } from '../lib/comparisons';

interface ComparisonTableProps {
  features: ComparisonFeature[];
  competitorName: string;
  title: string;
}

/**
 * Renders a value cell, converting "Yes"/"No" to icons for visual clarity.
 */
function CellValue({ value }: { value: string }) {
  const lowerValue = value.toLowerCase().trim();

  if (lowerValue === 'yes') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CheckCircleIcon color="success" fontSize="small" />
        <Typography variant="body2" component="span">
          {value}
        </Typography>
      </Box>
    );
  }

  if (lowerValue === 'no') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <CancelIcon color="error" fontSize="small" />
        <Typography variant="body2" component="span">
          {value}
        </Typography>
      </Box>
    );
  }

  return (
    <Typography variant="body2" component="span">
      {value}
    </Typography>
  );
}

/**
 * ComparisonTable - Side-by-side feature comparison table.
 * Renders as a standard table on desktop, switches to stacked cards on mobile
 * for improved readability.
 */
export function ComparisonTable({
  features,
  competitorName,
  title,
}: ComparisonTableProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!features || features.length === 0) {
    return null;
  }

  if (isMobile) {
    return (
      <Box sx={{ my: 3 }}>
        <Typography
          variant="h5"
          component="h2"
          sx={{ fontWeight: 600, mb: 2 }}
        >
          {title}
        </Typography>
        {features.map((feature, index) => (
          <Paper
            key={index}
            elevation={0}
            sx={{
              p: 2,
              mb: 1.5,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}
            >
              {feature.feature}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: 'primary.main', fontWeight: 600 }}
                >
                  Boty
                </Typography>
                <Box>
                  <CellValue value={feature.boty} />
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontWeight: 600 }}
                >
                  {competitorName}
                </Typography>
                <Box>
                  <CellValue value={feature.competitor} />
                </Box>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ my: 3 }}>
      <Typography
        variant="h5"
        component="h2"
        sx={{ fontWeight: 600, mb: 2 }}
      >
        {title}
      </Typography>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 700, width: '40%' }}>
                Feature
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  width: '30%',
                }}
              >
                Boty
              </TableCell>
              <TableCell sx={{ fontWeight: 700, width: '30%' }}>
                {competitorName}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {features.map((feature, index) => (
              <TableRow
                key={index}
                sx={{
                  '&:nth-of-type(even)': { bgcolor: 'grey.50' },
                }}
              >
                <TableCell sx={{ fontWeight: 500 }}>
                  {feature.feature}
                </TableCell>
                <TableCell>
                  <CellValue value={feature.boty} />
                </TableCell>
                <TableCell>
                  <CellValue value={feature.competitor} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
